import { Fragment, type ReactNode } from "react";

// A small Markdown renderer for the legal drafts we copy in (src/content/personalLegal): headings, paragraphs,
// bullet and numbered lists (with indented continuation lines), block quotes, tables, rules, and inline
// **bold**, *italic*, `code` and [links](url). It builds React elements (no HTML strings), so the text can
// never inject markup.

export type MdBlock =
  | { type: "heading"; level: 1 | 2 | 3; text: string }
  | { type: "paragraph"; text: string }
  | { type: "quote"; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "rule" };

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

export function parseMarkdown(src: string): MdBlock[] {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ type: "heading", level: h[1].length as 1 | 2 | 3, text: h[2].trim() });
      i += 1;
      continue;
    }
    if (/^-{3,}\s*$/.test(line)) {
      blocks.push({ type: "rule" });
      i += 1;
      continue;
    }
    if (line.startsWith(">")) {
      const parts: string[] = [];
      while (i < lines.length && lines[i].startsWith(">")) parts.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push({ type: "quote", text: parts.join(" ").trim() });
      continue;
    }
    if (line.trim().startsWith("|") && i + 1 < lines.length && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(cells(lines[i++]));
      blocks.push({ type: "table", head, rows });
      continue;
    }
    const li = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(line);
    if (li) {
      const ordered = /\d/.test(li[2]);
      const items: string[] = [];
      while (i < lines.length) {
        const m = /^\s*([-*]|\d+\.)\s+(.*)$/.exec(lines[i]);
        if (m && /\d/.test(m[1]) === ordered) {
          items.push(m[2]);
          i += 1;
        } else if (items.length && /^\s{2,}\S/.test(lines[i])) {
          items[items.length - 1] += ` ${lines[i].trim()}`;
          i += 1;
        } else break;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,3}\s|>|-{3,}\s*$|\s*([-*]|\d+\.)\s+)/.test(lines[i]) &&
      !lines[i].trim().startsWith("|")
    ) {
      para.push(lines[i++].trim());
    }
    // A line that starts with a bold label ("**Effective date:** …") starts a new line, as the drafts are written.
    if (para.length) blocks.push({ type: "paragraph", text: para.map((l, j) => (j > 0 && l.startsWith("**") ? `\n${l}` : l)).join(" ").replace(/ \n/g, "\n") });
    else i += 1;
  }
  return blocks;
}

export type LinkRenderer = (href: string, children: ReactNode, key: string) => ReactNode;

const defaultLink: LinkRenderer = (href, children, key) => (
  <a key={key} href={href} rel="noopener noreferrer">
    {children}
  </a>
);

/** Inline Markdown → React nodes. */
export function renderInline(text: string, link: LinkRenderer = defaultLink, keyPrefix = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|\*([^*\s][^*]*)\*)/g;
  let last = 0;
  let n = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${n++}`;
    if (m[2] !== undefined) out.push(<strong key={key}>{renderInline(m[2], link, key)}</strong>);
    else if (m[3] !== undefined) out.push(<code key={key}>{m[3]}</code>);
    else if (m[4] !== undefined) out.push(link(m[5], renderInline(m[4], link, key), key));
    else if (m[6] !== undefined) out.push(<em key={key}>{m[6]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Render parsed blocks. Headings shift by `headingOffset` (so a document's "#" can sit under the page's h1). */
export function MarkdownBlocks({ blocks, link, headingOffset = 0 }: { blocks: MdBlock[]; link?: LinkRenderer; headingOffset?: number }) {
  return (
    <>
      {blocks.map((b, i) => {
        const key = `b${i}`;
        const inline = (s: string, k = key) => renderInline(s, link, k);
        switch (b.type) {
          case "heading": {
            const level = Math.min(6, b.level + headingOffset);
            const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4";
            return <Tag key={key}>{inline(b.text)}</Tag>;
          }
          case "paragraph":
            return (
              <p key={key}>
                {b.text.split("\n").map((part, j) => (
                  <Fragment key={j}>
                    {j > 0 && <br />}
                    {inline(part, `${key}-${j}`)}
                  </Fragment>
                ))}
              </p>
            );
          case "quote":
            return <blockquote key={key}>{inline(b.text)}</blockquote>;
          case "rule":
            return <hr key={key} />;
          case "list": {
            const Tag = b.ordered ? "ol" : "ul";
            return (
              <Tag key={key}>
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it, `${key}-${j}`)}</li>
                ))}
              </Tag>
            );
          }
          case "table":
            return (
              <div key={key} className="md-table">
                <table>
                  <thead>
                    <tr>
                      {b.head.map((c, j) => (
                        <th key={j} scope="col">
                          {inline(c, `${key}-h${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>
                        {r.map((c, k) => (
                          <td key={k}>{inline(c, `${key}-${j}-${k}`)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return <Fragment key={key} />;
        }
      })}
    </>
  );
}
